import React, { useState } from 'react';
import { Card, Badge, Row, Col } from 'react-bootstrap';
import { format } from 'date-fns';
import IconShow from "../../../images/fi_eye.png";
import IconEdit from "../../../images/edit.png";
import './KanbanView.css';

const KanbanView = ({ orders, onStatusChange }) => {
  const [draggedOrder, setDraggedOrder] = useState(null);

  // Définition des colonnes du Kanban par statut
  const columns = [
    { id: 4, title: 'En cours de validation', color: 'primary', icon: '✓' },
    { id: 3, title: 'En préparation', color: 'info', icon: '📦' },
    { id: 1, title: 'En cours de livraison', color: 'warning', icon: '🚚' },
    { id: 2, title: 'Livré', color: 'success', icon: '✅' },
    { id: 6, title: 'Annulé', color: 'danger', icon: '❌' },
  ];

  // Grouper les commandes par statut
  const groupedOrders = columns.reduce((acc, column) => {
    acc[column.id] = orders?.filter(order => order.StatuCommande === column.id) || [];
    return acc;
  }, {});

  // Gestion du drag & drop
  const handleDragStart = (e, order) => {
    setDraggedOrder(order);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    if (draggedOrder && draggedOrder.StatuCommande !== newStatus) {
      // Appeler la fonction de changement de statut
      if (onStatusChange) {
        onStatusChange(draggedOrder.id, newStatus);
      }
    }
    setDraggedOrder(null);
  };

  const handleDragEnd = () => {
    setDraggedOrder(null);
  };

  return (
    <div className="kanban-board">
      <Row className="g-3">
        {columns.map((column) => (
          <Col key={column.id} lg={4} md={6} sm={12}>
            <div
              className={`kanban-column border-${column.color}`}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              {/* En-tête de colonne */}
              <div className={`kanban-header bg-${column.color}`}>
                <h5 className="mb-0">
                  <span className="me-2">{column.icon}</span>
                  {column.title}
                  <Badge bg="light" text="dark" className="ms-2">
                    {groupedOrders[column.id]?.length || 0}
                  </Badge>
                </h5>
              </div>

              {/* Cartes des commandes */}
              <div className="kanban-cards">
                {groupedOrders[column.id]?.length === 0 ? (
                  <div className="text-muted text-center p-3">
                    <small>Aucune commande</small>
                  </div>
                ) : (
                  groupedOrders[column.id]?.map((order) => (
                    <Card
                      key={order.id}
                      className={`kanban-card ${draggedOrder?.id === order.id ? 'dragging' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, order)}
                      onDragEnd={handleDragEnd}
                    >
                      <Card.Body className="p-3">
                        {/* En-tête de la carte */}
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <strong className="text-primary">#{order.id}</strong>
                          <div>
                            <a href={`/commande-fournisseur/show/${order.id}`} className="me-2">
                              <img src={IconShow} width="16px" alt="Voir" />
                            </a>
                            <a href={`/commande-fournisseur/edit/${order.id}`}>
                              <img src={IconEdit} width="16px" alt="Modifier" />
                            </a>
                          </div>
                        </div>

                        {/* Badge Type Client */}
                        <div className="mb-2">
                          <Badge
                            bg={
                              order.client_type === 'fournisseur'
                                ? 'primary'
                                : order.client_type === 'particulier'
                                ? 'success'
                                : order.client_type === 'entreprise'
                                ? 'warning'
                                : 'secondary'
                            }
                            className={order.client_type === 'entreprise' ? 'text-dark' : ''}
                          >
                            {order.client_type === 'fournisseur' && '🏢 Fournisseur'}
                            {order.client_type === 'particulier' && '👤 Particulier'}
                            {order.client_type === 'entreprise' && '💼 Entreprise'}
                            {!order.client_type && '❓ Inconnu'}
                          </Badge>
                        </div>

                        {/* Informations de la commande */}
                        <div className="mb-2">
                          <div className="small fw-bold text-truncate">
                            {order.fournisseur_profil?.user_profil?.RaisonSocial || '—'}
                          </div>
                        </div>

                        {/* Date de livraison */}
                        <div className="small text-muted mb-2">
                          <strong>📅 Livraison:</strong>{' '}
                          {order.DateLivraison
                            ? format(new Date(order.DateLivraison), 'dd/MM/yyyy HH:mm')
                            : '—'}
                        </div>

                        {/* Villes */}
                        <div className="small text-muted">
                          <div className="d-flex justify-content-between">
                            <span>
                              📍 {order.AdressePickUp?.split(',')[1]?.trim() || '—'}
                            </span>
                            <span>→</span>
                            <span>
                              📍 {order.AdresseLivraison?.split(',')[1]?.trim() || '—'}
                            </span>
                          </div>
                        </div>

                        {/* Livreur */}
                        {order.livreur_profil && (
                          <div className="small text-muted mt-2">
                            🚚{' '}
                            {`${order.livreur_profil.nom || ''} ${order.livreur_profil.prenom || ''}`.trim()}
                          </div>
                        )}

                        {/* Tarif */}
                        {order.livraisonHT && (
                          <div className="small fw-bold text-end mt-2">
                            {order.livraisonHT} €
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default KanbanView;
